import * as Blockly from 'blockly-tradais';
import { AtlasFieldBridge } from './atlas-field-bridge';
import { javascriptGenerator } from 'blockly-tradais/javascript';

export const QueryFns = {
    customBlockForQuery() {
        // Define 'set_variable' block
        Blockly.Blocks['get_query'] = {
          init: function () {
            this.appendDummyInput()
              .appendField('Get Entity:')
              .appendField(
                new NonEditableFieldVariable(
                  'NANENTITY',
                  undefined,
                  ['ENTITYNAME'],
                  'ENTITYNAME'
                ),
                'VAR'
              );
            // Phase 3 (order builder): ORDER BY socket removed — sorting is now done with the add_order
            // blocks (overlaid via mergeOrderBuffer). Old saved fns run unchanged; on edit, an old wired
            // order block orphans (redo with add_order).
            this.appendValueInput('C').appendField('Limit:').setCheck('Number');
            this.appendValueInput('D').appendField('Off Set:').setCheck('Number');
            // Phase 3.5: WHERE / Child Entities / Sibling sockets removed. Filtering (parent / child /
            // sibling) is now done with the Conditions blocks (add_condition), overlaid at query time via
            // mergeConditionBuffer. Old saved functions still RUN (their saved JS is frozen); on edit, any
            // old wired condition blocks orphan (visible + detached) — redo the filter with Conditions blocks.
            this.setOutput(true, 'ENTITY_LIST');
            this.setColour('#030303');
            this.setTooltip('select entity to be retrevied');
            this.setHelpUrl('');
          },
        };
        (javascriptGenerator as any).forBlock['get_query'] = function (block: any) {
          var entityId = (javascriptGenerator as any).nameDB_.getName(
            block.getFieldValue('VAR'),
            'VARIABLE'
          );
    
          var limitValue =
            (javascriptGenerator as any).valueToCode(
              block,
              'C',
              (javascriptGenerator as any).ORDER_ASSIGNMENT
            ) || 0;
          var offSetValue =
            (javascriptGenerator as any).valueToCode(
              block,
              'D',
              (javascriptGenerator as any).ORDER_ASSIGNMENT
            ) || 0;
          // Phase 3: order_by + where now come from the imperative buffers (mergeOrderBuffer /
          // mergeConditionBuffer below), NOT from sockets. The B (ORDER BY) / CONDITIONS / EC / E
          // inputs were removed from the block, so reading them via valueToCode throws
          // ("Input 'B' doesn't exist on 'get_query'"). Only limit + offset remain inline.
          var code = '';
          var finalCode =
            entityId +
            ': {' +
            (limitValue == 0 ? '' : 'limit: ' + limitValue + ',') +
            (offSetValue == 0 ? '' : 'offset: ' + offSetValue + ',') +
            ' }, ';
          if (limitValue == 0 && offSetValue == 0) {
            finalCode = '';
          }
          if (block.getParent()?.type == 'get_query') {
            code = entityId == 'NANENTITY' ? ' ' : finalCode;
          } else {
            // Wrap the variables bag so the imperative condition + order buffers are overlaid at
            // runtime (where + order_by). Empty buffers → unchanged (old fns safe).
            code =
              "await this.query(this.getEntityById('" +
              entityId +
              "'), this.mergeOrderBuffer(this.mergeConditionBuffer({" +
              (entityId == 'NANENTITY' ? ' ' : finalCode) +
              ' })))';
          }
          return [code, (javascriptGenerator as any).ORDER_ATOMIC];
        };
    
        class NonEditableFieldVariable extends Blockly.FieldVariable {
          constructor(
            variableName: any,
            validator: any,
            variableTypes: any,
            defaultType: any
          ) {
            super(variableName, validator, variableTypes, defaultType);
          }
    
          // Override the showEditor_ method to disable editing
          override showEditor_() {
            // click the dropdown → open Atlas (same action as the swap-variable button)
            AtlasFieldBridge.open && AtlasFieldBridge.open((this as any).getSourceBlock());
          }
        }
      } ,
        /**
         * Phase 2 — entity aggregate query block.
         *
         * Mirrors `get_query`'s shape but emits a Hasura `_aggregate` query
         * via CrudService.aggregateEntity instead of the row-list query.
         * Returns a Number scalar (not an entity list).
         *
         * Inputs:
         *   - VAR    — entity name (NonEditableFieldVariable<ENTITYNAME>);
         *              picked via the AGGREGATE_ENTITY HTML side panel
         *   - OP     — avg / sum / min / max / count
         *   - COLUMN — typed column to aggregate (NonEditableFieldVariable<NUMBER>);
         *              required for avg/sum/min/max, ignored for count
         *   - CONDITIONS — where-clause socket, accepts OR / QUERY_VARIABLE
         *   - EC         — child entity conditions socket
         *
         * Code emitted:
         *   await this.aggregateEntity(
         *     this.getEntityById('<entityId>'),
         *     '<op>',
         *     '<column>' | null,
         *     { <entityId>: { where: { ... } } }
         *   )
         *
         * See docs/plans/query-capabilities-phase-2.md §4.4 (block design)
         * and §6.1 (CrudService.aggregateEntity).
         */
        customBlockForEntityAggregateQuery() {
          Blockly.Blocks['entity_aggregate_query'] = {
            init: function () {
              this.appendDummyInput()
                .appendField('Aggregate Entity:')
                .appendField(
                  new NonEditableFieldVariable(
                    'NANENTITY',
                    undefined,
                    ['ENTITYNAME'],
                    'ENTITYNAME'
                  ),
                  'VAR'
                );
              this.appendDummyInput('OP_INPUT')
                .appendField('Op:')
                .appendField(
                  new Blockly.FieldDropdown([
                    ['average', 'avg'],
                    ['sum', 'sum'],
                    ['min', 'min'],
                    ['max', 'max'],
                    ['count', 'count'],
                  ]),
                  'OP'
                );
              this.appendDummyInput('COLUMN_INPUT')
                .appendField('Column:')
                .appendField(
                  new NonEditableFieldVariable(
                    'NANENTITYNUMBER',
                    undefined,
                    ['NUMBER'],
                    'NUMBER'
                  ),
                  'COLUMN'
                );
              // Phase 3.5: WHERE / Child sockets removed — filter the aggregate with the Conditions blocks
              // (add_condition), overlaid via mergeConditionBuffer (same as get_query).
              this.setOutput(true, 'Number');
              this.setColour('#5b80a5');
              this.setTooltip('Aggregate (avg/sum/min/max/count) over an entity');
              this.setHelpUrl('');
            },
          };
          (javascriptGenerator as any).forBlock['entity_aggregate_query'] = function (block: any) {
            var entityId = block.getFieldValue('VAR');
            var op = block.getFieldValue('OP');
            var columnRaw = (block.getFieldValue('COLUMN') || '').replace('__AGG_NUM', '');
            var columnName = '';
            if (columnRaw && columnRaw !== 'NANENTITYNUMBER') {
              const parts = columnRaw.split('$');
              columnName = parts[parts.length - 1];
            }
            if (entityId === 'NANENTITY') {
              return ['null', (javascriptGenerator as any).ORDER_ATOMIC];
            }
            if (op !== 'count' && !columnName) {
              // Non-count op without a column — emit null so callers don't blow up
              return ['null', (javascriptGenerator as any).ORDER_ATOMIC];
            }

            // Phase 3: the WHERE comes from the imperative condition buffer (mergeConditionBuffer) — the
            // CONDITIONS / EC sockets were removed, so reading them via valueToCode would throw. The static
            // bag is empty; the buffer overlays the where (empty buffer ⇒ old aggregates behave identically).
            var columnArg = op === 'count' ? 'null' : `'${columnName}'`;

            var code =
              `await this.aggregateEntity(this.getEntityById('${entityId}'), '${op}', ${columnArg}, this.mergeConditionBuffer({}))`;
            return [code, (javascriptGenerator as any).ORDER_ATOMIC];
          };

          class NonEditableFieldVariable extends Blockly.FieldVariable {
            constructor(
              variableName: any,
              validator: any,
              variableTypes: any,
              defaultType: any
            ) {
              super(variableName, validator, variableTypes, defaultType);
            }
            override showEditor_() {
            // click the dropdown → open Atlas (same action as the swap-variable button)
            AtlasFieldBridge.open && AtlasFieldBridge.open((this as any).getSourceBlock());
          }
          }
        },
       customBlockForSetOrderByVariable() {
          Blockly.Blocks['get_set_order_variable'] = {
            init: function () {
              this.appendDummyInput()
                .appendField('get order')
                .appendField(
                  new NonEditableFieldVariable(
                    'order_by',
                    undefined,
                    ['NUMBER', 'STRING', 'TIMESTAMP', 'BOOLEAN', 'ENTITYCHILDREN'],
                    'NUMBER'
                  ),
                  'VAR'
                );
              // .appendField(new Blockly.FieldLabel('localVariable$defaultFlag'), 'VAR');
              // .appendField(new Blockly.FieldVariable('_0_', undefined, ['Boolean'], 'Boolean'), 'VAR');
      
              this.appendDummyInput('SET')
                .appendField('order: ')
                .appendField(
                  new Blockly.FieldDropdown([
                    ['Ascending', 'asc'],
                    ['Ascending with nulls last', 'asc_nulls_last'],
                    ['Ascending with nulls first', 'asc_nulls_first'],
                    ['Descending', 'desc'],
                    ['Descending with nulls last', 'desc_nulls_last'],
                    ['Descending with nulls first', 'desc_nulls_first'],
                  ]),
                  'DROPDOWN'
                );
      
              this.setOutput(true, 'ORDER');
              this.setColour(0);
              this.setTooltip('Get the value of a order variable');
              this.setHelpUrl('');
            },
          };
          (javascriptGenerator as any).forBlock['get_set_order_variable'] = function (
            block: any
          ) {
            var variableName = (javascriptGenerator as any).nameDB_.getName(
              block.getFieldValue('VAR'),
              'VARIABLE'
            );
            var variableName = block.getFieldValue('VAR');
      
            var dropdownValue = block.getFieldValue('DROPDOWN');
            var code = ' { ' + variableName + ': `' + dropdownValue + '`} ';
            if (variableName == 'order_by') {
              code = '';
            }
            return [code, (javascriptGenerator as any).ORDER_ATOMIC];
          };
      
          class NonEditableFieldVariable extends Blockly.FieldVariable {
            constructor(
              variableName: any,
              validator: any,
              variableTypes: any,
              defaultType: any
            ) {
              super(variableName, validator, variableTypes, defaultType);
            }
      
            // Override the showEditor_ method to disable editing
            override showEditor_() {
            // click the dropdown → open Atlas (same action as the swap-variable button)
            AtlasFieldBridge.open && AtlasFieldBridge.open((this as any).getSourceBlock());
          }
          }
        } ,
          customBlockForEntityConditionsForQuery() {
            // Define 'set_variable' block
            Blockly.Blocks['entity_conditions'] = {
              init: function () {
                this.appendDummyInput()
                  .appendField('Condition For Child Entity:')
                  .appendField(
                    new NonEditableFieldVariable(
                      'NANENTITY',
                      undefined,
                      ['ENTITYNAME'],
                      'ENTITYNAME'
                    ),
                    'VAR'
                  );
                this.appendValueInput('B').appendField('ORDER BY:').setCheck('ORDER');
                // this.setInputsInline(true);
                this.appendValueInput('C').appendField('Limit:').setCheck('Number');
                this.appendValueInput('D').appendField('Off Set:').setCheck('Number');
                this.appendValueInput('CONDITIONS')
                  .setCheck(['OR', 'QUERY_VARIABLE'])
                  .appendField('Where Conditions');
                this.setOutput(true, 'ENTITYCONDITIONS');
                this.appendValueInput('E')
                  .appendField('Sibling Entity:')
                  .setCheck('ENTITYCONDITIONS');
                this.setColour('#808080');
                this.setTooltip('select entity to be retrevied');
                this.setHelpUrl('');
              },
            };
            (javascriptGenerator as any).forBlock['entity_conditions'] = function (
              block: any
            ) {
              var entityId = (javascriptGenerator as any).nameDB_.getName(
                block.getFieldValue('VAR'),
                'VARIABLE'
              );
              var limitValue =
                (javascriptGenerator as any).valueToCode(
                  block,
                  'C',
                  (javascriptGenerator as any).ORDER_ASSIGNMENT
                ) || '';
              var offSetValue =
                (javascriptGenerator as any).valueToCode(
                  block,
                  'D',
                  (javascriptGenerator as any).ORDER_ASSIGNMENT
                ) || 0;
              var orderByValue =
                (javascriptGenerator as any).valueToCode(
                  block,
                  'B',
                  (javascriptGenerator as any).ORDER_ASSIGNMENT
                ) || '0';
              var conditions =
                (javascriptGenerator as any).valueToCode(
                  block,
                  'CONDITIONS',
                  (javascriptGenerator as any).ORDER_ASSIGNMENT
                ) || '';
              var additionalSiblingConditions =
                (javascriptGenerator as any).valueToCode(
                  block,
                  'E',
                  (javascriptGenerator as any).ORDER_ASSIGNMENT
                ) || '';
              var finalCode =
                entityId +
                ': {' +
                (limitValue == 0 ? '' : 'limit: ' + limitValue + ',') +
                (offSetValue == 0 ? '' : 'offset: ' + offSetValue + ',') +
                (orderByValue == '0' ? '' : 'order_by: ' + orderByValue + ',') +
                conditions +
                ' }, ';
              if (
                limitValue == 0 &&
                offSetValue == 0 &&
                orderByValue == '0' &&
                conditions == ''
              ) {
                finalCode = '';
              }
        
              return [
                (entityId == 'NANENTITY' ? ' ' : finalCode) +
                  additionalSiblingConditions,
                (javascriptGenerator as any).ORDER_ATOMIC,
              ];
            };
        
            class NonEditableFieldVariable extends Blockly.FieldVariable {
              constructor(
                variableName: any,
                validator: any,
                variableTypes: any,
                defaultType: any
              ) {
                super(variableName, validator, variableTypes, defaultType);
              }
        
              // Override the showEditor_ method to disable editing
              override showEditor_() {
            // click the dropdown → open Atlas (same action as the swap-variable button)
            AtlasFieldBridge.open && AtlasFieldBridge.open((this as any).getSourceBlock());
          }
            }
          } ,
            customBlockForOrConditionInQuery() {
              // Define 'set_variable' block
              Blockly.Blocks['entity_or_conditions'] = {
                init: function () {
                  this.appendDummyInput().appendField('Or');
          
                  this.appendValueInput('CHILD_CONDITIONS')
                    .setCheck(['QUERY_VARIABLE', 'OR'])
                    .appendField('Child:');
                  this.appendValueInput('SIBILING_CONDITIONS')
                    .setCheck(['QUERY_VARIABLE', 'OR'])
                    .appendField('Sibling');
                  this.setOutput(true, 'OR');
                  this.setColour('#5b80a5');
                  this.setTooltip('select entity to be retrevied');
                  this.setHelpUrl('');
                },
              };
              (javascriptGenerator as any).forBlock['entity_or_conditions'] = function (
                block: any
              ) {
                var sibblingCode =
                  (javascriptGenerator as any).valueToCode(
                    block,
                    'SIBILING_CONDITIONS',
                    (javascriptGenerator as any).ORDER_ASSIGNMENT
                  ) || '';
          
                var childCode =
                  (javascriptGenerator as any).valueToCode(
                    block,
                    'CHILD_CONDITIONS',
                    (javascriptGenerator as any).ORDER_ASSIGNMENT
                  ) || '';
                if (childCode !== '') {
                  childCode = ' { ' + childCode + ' }, ';
                }
                if (sibblingCode !== '') {
                  sibblingCode = ' { ' + sibblingCode + ' }, ';
                }
                var finalCode = '_or: [' + childCode + ' ' + sibblingCode + ' ]';
                if (sibblingCode == '' && childCode == '') finalCode = '';
                return [finalCode, (javascriptGenerator as any).ORDER_ATOMIC];
              };
            },
                  customBlockForStringInConditionInQuery() {
                    // Define 'set_variable' block
                    Blockly.Blocks['entity_set_string_in_conditions'] = {
                      init: function () {
                        this.appendDummyInput()
                          .appendField('String: ')
                          .appendField(
                            new NonEditableFieldVariable(
                              'NANENTITYSTRING',
                              undefined,
                              ['STRING'],
                              'STRING'
                            ),
                            'VAR'
                          );
                
                        this.appendValueInput('SET').setCheck([
                          'STRINGCONDITIONS',
                          'GLOBALCONDITIONS',
                        ]);
                        this.appendValueInput('SIBILING_CONDITIONS')
                          .setCheck(['QUERY_VARIABLE', 'OR'])
                          .appendField('Sibling');
                        this.setOutput(true, 'QUERY_VARIABLE');
                        this.setColour('#5ba58c');
                        this.setTooltip('select entity to be retrevied');
                        this.setHelpUrl('');
                      },
                    };
                    (javascriptGenerator as any).forBlock['entity_set_string_in_conditions'] =
                      function (block: any) {
                        var entityNumber = (javascriptGenerator as any).nameDB_.getName(
                          block.getFieldValue('VAR'),
                          'VARIABLE'
                        );
                        var conditions =
                          (javascriptGenerator as any).valueToCode(
                            block,
                            'SET',
                            (javascriptGenerator as any).ORDER_ASSIGNMENT
                          ) || '';
                        var sibblingCode =
                          (javascriptGenerator as any).valueToCode(
                            block,
                            'SIBILING_CONDITIONS',
                            (javascriptGenerator as any).ORDER_ASSIGNMENT
                          ) || '';
                
                        var code = '';
                        if (entityNumber !== 'NANENTITYSTRING' && conditions !== ''){
                          if(entityNumber.includes('%')) {
                            var entityTemp = entityNumber.split('%');
                            for(let c =1 ; c < entityTemp.length ; c++) {
                              code = entityTemp[c] + ': {';
                            }
                            code = code + entityTemp[0].split('$')[0] + ': { ' + conditions + ' } ,';
                             for(let c =1 ; c < entityTemp.length ; c++) {
                              code = code + ' } , ';
                            }
                
                          } else {
                          code = entityNumber.split('$')[0] + ': {' + conditions + ' }, ';
                          }}
                        return [code + sibblingCode, (javascriptGenerator as any).ORDER_ATOMIC];
                      };
                    class NonEditableFieldVariable extends Blockly.FieldVariable {
                      constructor(
                        variableName: any,
                        validator: any,
                        variableTypes: any,
                        defaultType: any
                      ) {
                        super(variableName, validator, variableTypes, defaultType);
                      }
                
                      // Override the showEditor_ method to disable editing
                      override showEditor_() {
            // click the dropdown → open Atlas (same action as the swap-variable button)
            AtlasFieldBridge.open && AtlasFieldBridge.open((this as any).getSourceBlock());
          }
                    }
                  },
                    customBlockForStringConditionsInQuery() {
                      // Define 'set_variable' block
                      Blockly.Blocks['entity_string_conditions'] = {
                        init: function () {
                          this.appendValueInput('SET')
                            .setCheck(['String'])
                            .appendField(
                              new Blockly.FieldDropdown([
                                ['=', '_eq'],
                                ['!=', '_neq'],
                                ['contains', '_ilike'],
                              ]),
                              'DROPDOWN'
                            );
                  
                          this.appendValueInput('SIBILING_CONDITIONS')
                            .setCheck(['GLOBALCONDITIONS', 'STRINGCONDITIONS'])
                            .appendField('Sibling');
                  
                          this.setOutput(true, 'STRINGCONDITIONS');
                          this.setColour('#5ba58c');
                          this.setTooltip('select entity to be retrevied');
                          this.setHelpUrl('');
                        },
                      };
                      (javascriptGenerator as any).forBlock['entity_string_conditions'] =
                        function (block: any) {
                          var dropdownValue = block.getFieldValue('DROPDOWN');
                  
                          var sibblingCode =
                            (javascriptGenerator as any).valueToCode(
                              block,
                              'SIBILING_CONDITIONS',
                              (javascriptGenerator as any).ORDER_ASSIGNMENT
                            ) || '';
                  
                          var value =
                            (javascriptGenerator as any).valueToCode(
                              block,
                              'SET',
                              (javascriptGenerator as any).ORDER_ASSIGNMENT
                            ) || '';
                          var code = '';
                          if (value !== '') {
                            // Only wrap with #LIKE for contains operator
                            if (dropdownValue === '_contains' || dropdownValue === '_ilike') {
                              value = '"#LIKE" + ' + value + ' + "#LIKE"';
                            }
                            code = dropdownValue + ': ' + value + ',';
                          }
                          if (sibblingCode.includes(dropdownValue)) sibblingCode = '';
                          return [code + sibblingCode, (javascriptGenerator as any).ORDER_ATOMIC];
                        };
                    },
                      customBlockForTimeStampInConditionInQuery() {
                // Define 'set_variable' block
                Blockly.Blocks['entity_set_time_stamp_in_conditions'] = {
                  init: function () {
                    this.appendDummyInput()
                      .appendField('Time Stamp: ')
                      .appendField(
                        new NonEditableFieldVariable(
                          'NANENTITYTIMESTAMP',
                          undefined,
                          ['TIMESTAMP'],
                          'TIMESTAMP'
                        ),
                        'VAR'
                      );
            
                    this.appendValueInput('SET').setCheck([
                      'TIMESTAMPCONDITIONS',
                      'GLOBALCONDITIONS',
                    ]);
                    this.appendValueInput('SIBILING_CONDITIONS')
                      .setCheck(['QUERY_VARIABLE', 'OR'])
                      .appendField('Sibling');
                    this.setOutput(true, 'QUERY_VARIABLE');
                    this.setColour('#F5A623');
                    this.setTooltip('select entity to be retrevied');
                    this.setHelpUrl('');
                  },
                };
                (javascriptGenerator as any).forBlock['entity_set_time_stamp_in_conditions'] =
                  function (block: any) {
                    var entityNumber = (javascriptGenerator as any).nameDB_.getName(
                      block.getFieldValue('VAR'),
                      'VARIABLE'
                    );
                    var conditions =
                      (javascriptGenerator as any).valueToCode(
                        block,
                        'SET',
                        (javascriptGenerator as any).ORDER_ASSIGNMENT
                      ) || '';
                    var sibblingCode =
                      (javascriptGenerator as any).valueToCode(
                        block,
                        'SIBILING_CONDITIONS',
                        (javascriptGenerator as any).ORDER_ASSIGNMENT
                      ) || '';
            
                    var code = '';
                    if (entityNumber !== 'NANENTITYNUMBER' && conditions !== ''){
                      if(entityNumber.includes('%')) {
                        var entityTemp = entityNumber.split('%');
                        for(let c =1 ; c < entityTemp.length ; c++) {
                          code = entityTemp[c] + ': {';
                        }
                        code = code + entityTemp[0].split('$')[0] + ': { ' + conditions + ' } ,';
                         for(let c =1 ; c < entityTemp.length ; c++) {
                          code = code + ' } , ';
                        }
                      } else {
                      code = entityNumber.split('$')[0] + ': {' + conditions + ' }, ';
                      }}
                    return [code + sibblingCode, (javascriptGenerator as any).ORDER_ATOMIC];
                  };
                class NonEditableFieldVariable extends Blockly.FieldVariable {
                  constructor(
                    variableName: any,
                    validator: any,
                    variableTypes: any,
                    defaultType: any
                  ) {
                    super(variableName, validator, variableTypes, defaultType);
                  }
            
                  // Override the showEditor_ method to disable editing
                  override showEditor_() {
            // click the dropdown → open Atlas (same action as the swap-variable button)
            AtlasFieldBridge.open && AtlasFieldBridge.open((this as any).getSourceBlock());
          }
                }
              },
               customBlockForTimeStampConditionsInQuery() {
                  // Define 'set_variable' block
                  Blockly.Blocks['entity_time_stamp_conditions'] = {
                    init: function () {
                      this.appendValueInput('SET')
                        .setCheck(['TimeStamp'])
                        .appendField(
                          new Blockly.FieldDropdown([
                            ['=', '_eq'],
                            ['>', '_gt'],
                            ['>=', '_gte'],
                            ['<', '_lt'],
                            ['<=', '_lte'],
                            ['!=', '_neq'],
                          ]),
                          'DROPDOWN'
                        );
              
                      this.appendValueInput('SIBILING_CONDITIONS')
                        .setCheck(['GLOBALCONDITIONS', 'TIMESTAMPCONDITIONS'])
                        .appendField('Sibling');
              
                      this.setOutput(true, 'TIMESTAMPCONDITIONS');
                      this.setColour('#F5A623');
                      this.setTooltip('select entity to be retrevied');
                      this.setHelpUrl('');
                    },
                  };
                  (javascriptGenerator as any).forBlock['entity_time_stamp_conditions'] =
                    function (block: any) {
                      var dropdownValue = block.getFieldValue('DROPDOWN');
              
                      var sibblingCode =
                        (javascriptGenerator as any).valueToCode(
                          block,
                          'SIBILING_CONDITIONS',
                          (javascriptGenerator as any).ORDER_ASSIGNMENT
                        ) || '';
              
                      var value =
                        (javascriptGenerator as any).valueToCode(
                          block,
                          'SET',
                          (javascriptGenerator as any).ORDER_ASSIGNMENT
                        ) || '';
                      var code = '';
                      if (value !== '') code = dropdownValue + ': ' + value + ',';
                      if (sibblingCode.includes(dropdownValue)) sibblingCode = '';
                      return [code + sibblingCode, (javascriptGenerator as any).ORDER_ATOMIC];
                    };
                },
                      customBlockForNumberInConditionInQuery() {
                // Define 'set_variable' block
                Blockly.Blocks['entity_set_number_in_conditions'] = {
                  init: function () {
                    this.appendDummyInput()
                      .appendField('Number: ')
                      .appendField(
                        new NonEditableFieldVariable(
                          'NANENTITYNUMBER',
                          undefined,
                          ['NUMBER'],
                          'NUMBER'
                        ),
                        'VAR'
                      );
            
                    this.appendValueInput('SET').setCheck([
                      'NUMBERCONDITIONS',
                      'GLOBALCONDITIONS',
                    ]);
                    this.appendValueInput('SIBILING_CONDITIONS')
                      .setCheck(['QUERY_VARIABLE', 'OR'])
                      .appendField('Sibling');
                    this.setOutput(true, 'QUERY_VARIABLE');
                    this.setColour(230);
                    this.setTooltip('select entity to be retrevied');
                    this.setHelpUrl('');
                  },
                };
                (javascriptGenerator as any).forBlock['entity_set_number_in_conditions'] =
                  function (block: any) {
                    var entityNumber = (javascriptGenerator as any).nameDB_.getName(
                      block.getFieldValue('VAR'),
                      'VARIABLE'
                    );
                    var conditions =
                      (javascriptGenerator as any).valueToCode(
                        block,
                        'SET',
                        (javascriptGenerator as any).ORDER_ASSIGNMENT
                      ) || '';
                    var sibblingCode =
                      (javascriptGenerator as any).valueToCode(
                        block,
                        'SIBILING_CONDITIONS',
                        (javascriptGenerator as any).ORDER_ASSIGNMENT
                      ) || '';
            
                    var code = '';
                    if (entityNumber !== 'NANENTITYNUMBER' && conditions !== ''){
                      if(entityNumber.includes('%')) {
                        var entityTemp = entityNumber.split('%');
                        for(let c =1 ; c < entityTemp.length ; c++) {
                          code = entityTemp[c] + ': {';
                        }
                        code = code + entityTemp[0].split('$')[0] + ': { ' + conditions + ' } ,';
                         for(let c =1 ; c < entityTemp.length ; c++) {
                          code = code + ' } , ';
                        }
                      } else {
                      code = entityNumber.split('$')[0] + ': {' + conditions + ' }, ';
                      }}
                    return [code + sibblingCode, (javascriptGenerator as any).ORDER_ATOMIC];
                  };
                class NonEditableFieldVariable extends Blockly.FieldVariable {
                  constructor(
                    variableName: any,
                    validator: any,
                    variableTypes: any,
                    defaultType: any
                  ) {
                    super(variableName, validator, variableTypes, defaultType);
                  }
            
                  // Override the showEditor_ method to disable editing
                  override showEditor_() {
            // click the dropdown → open Atlas (same action as the swap-variable button)
            AtlasFieldBridge.open && AtlasFieldBridge.open((this as any).getSourceBlock());
          }
                }
              },
               customBlockForNumberConditionsInQuery() {
                  // Define 'set_variable' block
                  Blockly.Blocks['entity_number_conditions'] = {
                    init: function () {
                      this.appendValueInput('SET')
                        .setCheck(['Number'])
                        .appendField(
                          new Blockly.FieldDropdown([
                            ['=', '_eq'],
                            ['>', '_gt'],
                            ['>=', '_gte'],
                            ['<', '_lt'],
                            ['<=', '_lte'],
                            ['!=', '_neq'],
                          ]),
                          'DROPDOWN'
                        );
              
                      this.appendValueInput('SIBILING_CONDITIONS')
                        .setCheck(['GLOBALCONDITIONS', 'NUMBERCONDITIONS'])
                        .appendField('Sibling');
              
                      this.setOutput(true, 'NUMBERCONDITIONS');
                      this.setColour(230);
                      this.setTooltip('select entity to be retrevied');
                      this.setHelpUrl('');
                    },
                  };
                  (javascriptGenerator as any).forBlock['entity_number_conditions'] =
                    function (block: any) {
                      var dropdownValue = block.getFieldValue('DROPDOWN');
              
                      var sibblingCode =
                        (javascriptGenerator as any).valueToCode(
                          block,
                          'SIBILING_CONDITIONS',
                          (javascriptGenerator as any).ORDER_ASSIGNMENT
                        ) || '';
              
                      var value =
                        (javascriptGenerator as any).valueToCode(
                          block,
                          'SET',
                          (javascriptGenerator as any).ORDER_ASSIGNMENT
                        ) || '';
                      var code = '';
                      if (value !== '') code = dropdownValue + ': ' + value + ',';
                      if (sibblingCode.includes(dropdownValue)) sibblingCode = '';
                      return [code + sibblingCode, (javascriptGenerator as any).ORDER_ATOMIC];
                    };
                },
                      customBlockForBooleanInConditionInQuery() {
                        // Define 'set_variable' block
                        Blockly.Blocks['entity_set_boolean_in_conditions'] = {
                          init: function () {
                            this.appendDummyInput()
                              .appendField('Boolean: ')
                              .appendField(
                                new NonEditableFieldVariable(
                                  'NANENTITYBOOLEAN',
                                  undefined,
                                  ['BOOLEAN'],
                                  'BOOLEAN'
                                ),
                                'VAR'
                              );
                    
                            this.appendValueInput('SET').setCheck([
                              'BOOLEANCONDITIONS',
                              'GLOBALCONDITIONS',
                            ]);
                            this.appendValueInput('SIBILING_CONDITIONS')
                              .setCheck(['QUERY_VARIABLE', 'OR'])
                              .appendField('Sibling');
                            this.setOutput(true, 'QUERY_VARIABLE');
                            this.setColour(0);
                            this.setTooltip('select entity to be retrevied');
                            this.setHelpUrl('');
                          },
                        };
                        (javascriptGenerator as any).forBlock['entity_set_boolean_in_conditions'] =
                          function (block: any) {
                            var entityNumber = (javascriptGenerator as any).nameDB_.getName(
                              block.getFieldValue('VAR'),
                              'VARIABLE'
                            );
                            var conditions =
                              (javascriptGenerator as any).valueToCode(
                                block,
                                'SET',
                                (javascriptGenerator as any).ORDER_ASSIGNMENT
                              ) || '';
                            var sibblingCode =
                              (javascriptGenerator as any).valueToCode(
                                block,
                                'SIBILING_CONDITIONS',
                                (javascriptGenerator as any).ORDER_ASSIGNMENT
                              ) || '';
                    
                            var code = '';
                            if (entityNumber !== 'NANENTITYBOOLEAN' && conditions !== '') {
                              if(entityNumber.includes('%')) {
                                var entityTemp = entityNumber.split('%');
                                for(let c =1 ; c < entityTemp.length ; c++) {
                                  code = entityTemp[c] + ': {';
                                }
                                code = code + entityTemp[0].split('$')[0] + ': { ' + conditions + ' } ,';
                                 for(let c =1 ; c < entityTemp.length ; c++) {
                                  code = code + ' } , ';
                                }
                              } else {
                              code = entityNumber.split('$')[0] + ': {' + conditions + ' }, ';
                              }
                            }
                            return [code + sibblingCode, (javascriptGenerator as any).ORDER_ATOMIC];
                          };
                        class NonEditableFieldVariable extends Blockly.FieldVariable {
                          constructor(
                            variableName: any,
                            validator: any,
                            variableTypes: any,
                            defaultType: any
                          ) {
                            super(variableName, validator, variableTypes, defaultType);
                          }
                    
                          // Override the showEditor_ method to disable editing
                          override showEditor_() {
            // click the dropdown → open Atlas (same action as the swap-variable button)
            AtlasFieldBridge.open && AtlasFieldBridge.open((this as any).getSourceBlock());
          }
                        }
                      },
                       customBlockForBooleanConditionsInQuery() {
                          // Define 'set_variable' block
                          Blockly.Blocks['entity_boolean_conditions'] = {
                            init: function () {
                              this.appendValueInput('SET')
                                .setCheck(['Boolean'])
                                .appendField(
                                  new Blockly.FieldDropdown([
                                    ['=', '_eq'],
                                    ['!=', '_neq'],
                                  ]),
                                  'DROPDOWN'
                                );
                      
                              this.appendValueInput('SIBILING_CONDITIONS')
                                .setCheck(['GLOBALCONDITIONS', 'BOOLEANCONDITIONS'])
                                .appendField('Sibling');
                      
                              this.setOutput(true, 'BOOLEANCONDITIONS');
                              this.setColour(0);
                              this.setTooltip('select entity to be retrevied');
                              this.setHelpUrl('');
                            },
                          };
                          (javascriptGenerator as any).forBlock['entity_boolean_conditions'] =
                            function (block: any) {
                              var dropdownValue = block.getFieldValue('DROPDOWN');
                      
                              var sibblingCode =
                                (javascriptGenerator as any).valueToCode(
                                  block,
                                  'SIBILING_CONDITIONS',
                                  (javascriptGenerator as any).ORDER_ASSIGNMENT
                                ) || '';
                      
                              var value =
                                (javascriptGenerator as any).valueToCode(
                                  block,
                                  'SET',
                                  (javascriptGenerator as any).ORDER_ASSIGNMENT
                                ) || '';
                              var code = '';
                              if (value !== '') code = dropdownValue + ': ' + value + ',';
                              if (sibblingCode.includes(dropdownValue)) sibblingCode = '';
                              return [code + sibblingCode, (javascriptGenerator as any).ORDER_ATOMIC];
                            };
                        },
                         customBlockForIdInConditionInQuery() {
                    // Define 'set_variable' block
                    Blockly.Blocks['entity_set_id_in_conditions'] = {
                      init: function () {
                        this.appendDummyInput()
                          .appendField('ID: ')
                          .appendField(
                            new NonEditableFieldVariable(
                              'NAN',
                              undefined,
                              ['ENTITY_REFERENCE'],
                              'ENTITY_REFERENCE'
                            ),
                            'VAR'
                          );
                
                        this.appendValueInput('SET').setCheck([
                          'ENTITYREFERENCECONDITIONS',
                          'GLOBALCONDITIONS',
                        ]);
                        this.appendValueInput('SIBILING_CONDITIONS')
                          .setCheck(['QUERY_VARIABLE', 'OR'])
                          .appendField('Sibling');
                        this.setOutput(true, 'QUERY_VARIABLE');
                        this.setColour('#745ba5');
                        this.setTooltip('select entity to be retrevied');
                        this.setHelpUrl('');
                      },
                    };
                    (javascriptGenerator as any).forBlock['entity_set_id_in_conditions'] =
                      function (block: any) {
                        var entityNumber = (javascriptGenerator as any).nameDB_.getName(
                          block.getFieldValue('VAR'),
                          'VARIABLE'
                        );
                        var conditions =
                          (javascriptGenerator as any).valueToCode(
                            block,
                            'SET',
                            (javascriptGenerator as any).ORDER_ASSIGNMENT
                          ) || '';
                        var sibblingCode =
                          (javascriptGenerator as any).valueToCode(
                            block,
                            'SIBILING_CONDITIONS',
                            (javascriptGenerator as any).ORDER_ASSIGNMENT
                          ) || '';
                
                        var code = '';
                        if(entityNumber.startsWith('$')) {
                          entityNumber = 'id';
                        }
                        if (entityNumber !== 'NANENTITYSTRING' && conditions !== ''){
                          if(entityNumber.includes('%')) {
                            var entityTemp = entityNumber.split('%');
                            //! this dosnt look completed
                            for(let c =1 ; c < entityTemp.length ; c++) {
                              code = entityTemp[c] + ': {';
                            }
                            code = code + entityTemp[0].split('$')[0] + ': { ' + conditions + ' } ,';
                             for(let c =1 ; c < entityTemp.length ; c++) {
                              code = code + ' } , ';
                            }
                
                          } else {
                          code = entityNumber.split('$')[0] + ': {' + conditions + ' }, ';
                          }}
                        return [code + sibblingCode, (javascriptGenerator as any).ORDER_ATOMIC];
                      };
                    class NonEditableFieldVariable extends Blockly.FieldVariable {
                      constructor(
                        variableName: any,
                        validator: any,
                        variableTypes: any,
                        defaultType: any
                      ) {
                        super(variableName, validator, variableTypes, defaultType);
                      }
                
                      // Override the showEditor_ method to disable editing
                      override showEditor_() {
            // click the dropdown → open Atlas (same action as the swap-variable button)
            AtlasFieldBridge.open && AtlasFieldBridge.open((this as any).getSourceBlock());
          }
                    }
                  },
                    customBlockForIdConditionsInQuery() {
                      // Define 'set_variable' block
                      Blockly.Blocks['entity_id_conditions'] = {
                        init: function () {
                          this.appendValueInput('SET')
                            .setCheck(['ENTITY'])
                            .appendField(
                              new Blockly.FieldDropdown([
                                ['=', '_eq'],
                                ['!=', '_neq'],
                              ]),
                              'DROPDOWN'
                            );
                  
                          this.appendValueInput('SIBILING_CONDITIONS')
                            .setCheck(['GLOBALCONDITIONS', 'ENTITYREFERENCECONDITIONS'])
                            .appendField('Sibling');
                  
                          this.setOutput(true, 'ENTITYREFERENCECONDITIONS');
                          this.setColour('#745ba5');
                          this.setTooltip('select entity to be retrevied');
                          this.setHelpUrl('');
                        },
                      };
                      (javascriptGenerator as any).forBlock['entity_id_conditions'] =
                        function (block: any) {
                          var dropdownValue = block.getFieldValue('DROPDOWN');
                  
                          var sibblingCode =
                            (javascriptGenerator as any).valueToCode(
                              block,
                              'SIBILING_CONDITIONS',
                              (javascriptGenerator as any).ORDER_ASSIGNMENT
                            ) || '';
                  
                          var value =
                            (javascriptGenerator as any).valueToCode(
                              block,
                              'SET',
                              (javascriptGenerator as any).ORDER_ASSIGNMENT
                            ) || '';
                          var code = '';
                          if (value !== '' && value !== 'NAN_ENTITY_VARIABLE') {
                            // Only wrap with #LIKE for contains operator
                            if (dropdownValue === '_contains' || dropdownValue === '_ilike') {
                              value = '"#LIKE" + ' + value + ' + "#LIKE"';
                            }
                            if(value.includes('%'))
                            code = dropdownValue + ': ' + value + '.id ,';
                          else 
                            code = dropdownValue + ': ' + value + '.data.id ,';
                          }
                          if (sibblingCode.includes(dropdownValue)) sibblingCode = '';
                          return [code + sibblingCode, (javascriptGenerator as any).ORDER_ATOMIC];
                        };
                    },
                          customBlockForIsNullConditionsInQuery() {
                            // Define 'set_variable' block
                            Blockly.Blocks['entity_is_null_conditions'] = {
                              init: function () {
                                this.appendDummyInput('SET')
                                  .appendField('IsNull: ')
                                  .appendField(
                                    new Blockly.FieldDropdown([
                                      ['True', 'true'],
                                      ['False', 'false'],
                                    ]),
                                    'DROPDOWN'
                                  );
                        
                                this.setOutput(true, 'GLOBALCONDITIONS');
                                this.setColour('#000000');
                                this.setTooltip('select entity to be retrevied');
                                this.setHelpUrl('');
                              },
                            };
                            (javascriptGenerator as any).forBlock['entity_is_null_conditions'] =
                              function (block: any) {
                                var dropdownValue = block.getFieldValue('DROPDOWN');
                        
                                return [
                                  '_is_null: ' + dropdownValue + ', ',
                                  (javascriptGenerator as any).ORDER_ATOMIC,
                                ];
                              };
                          },
                            customBlockForUpdateEntityMutationVariable() {
                              Blockly.Blocks['update_entity'] = {
                                init: function () {
                                  this.appendValueInput('VALUE')
                                    .setCheck('ENTITY')
                                    .appendField('update entity')
                                    .appendField('with');
                                   this.appendValueInput('ENTITY_REFERENCE_SELECTION')
                                    .setCheck('ENTITY_REFERENCE_SELECTION')
                                    .appendField('Entity Reference Selection: ');

                                  this.setPreviousStatement(true, null);
                                  this.setNextStatement(true, null);
                                  this.setColour('#cdd3b1');
                                  this.setTooltip('Set a Entity value to a variable');
                                  this.setHelpUrl('');
                                },
                              };
                          
                              (javascriptGenerator as any).forBlock['update_entity'] = function (
                                block: any
                              ) {
                                // var variableName = (javascriptGenerator as any).nameDB_.getName(
                                //   block.getFieldValue('VAR'), 'VARIABLE');
                                var value =
                                  (javascriptGenerator as any).valueToCode(
                                    block,
                                    'VALUE',
                                    (javascriptGenerator as any).ORDER_ASSIGNMENT
                                  ) || 'NAN';

                                  var entityReferenceSelection =
                                  (javascriptGenerator as any).valueToCode(
                                    block,
                                    'ENTITY_REFERENCE_SELECTION',
                                    (javascriptGenerator as any).ORDER_ASSIGNMENT
                                  ) || '';
                                // var value ='';
                                if (value !== 'NAN_ENTITY_VARIABLE') {
                                  // Match `set_entity_variable` behavior: local/global variables store the value under `.data`.
                                  const targetVar =
                                    value.includes('globalVariable$') ||
                                    value.includes('localVariable$')
                                      ? value + '.data'
                                      : value;

                                  return (
                                    'var updatedEntityUniqueForUpdateEntity = await this.updateEntity( this.getEntityById(' +
                                    value +
                                    '$entityId),' +
                                    value +
                                    '$entityId,' +
                                    value +
                                    '$data  , $$CRUD_ENTITY_REFERENCE_SELECTION_START$$' + entityReferenceSelection +
                                      '$$CRUD_ENTITY_REFERENCE_SELECTION_END$$);\n'   +
                                    value +
                                    '$data = updatedEntityUniqueForUpdateEntity.data;\n'+
                                    ' await this.reinitializeComponents(); '
                                  );
                                }
                          
                                return '';
                              };
                          
                              class NonEditableFieldVariable extends Blockly.FieldVariable {
                                constructor(
                                  variableName: any,
                                  validator: any,
                                  variableTypes: any,
                                  defaultType: any
                                ) {
                                  super(variableName, validator, variableTypes, defaultType);
                                }
                          
                                // Override the showEditor_ method to disable editing
                                override showEditor_() {
            // click the dropdown → open Atlas (same action as the swap-variable button)
            AtlasFieldBridge.open && AtlasFieldBridge.open((this as any).getSourceBlock());
          }
                              }
                            },
                              customBlockForCreateEntityMutationVariable() {
                                Blockly.Blocks['create_entity'] = {
                                  init: function () {
                                    // this.appendValueInput('VALUE')
                                    //     .setCheck('ENTITY')
                                    //     .appendField('create entity with');
                                    // .appendField(new NonEditableFieldVariable('NAN', undefined, ['ENTITYNAME'], 'ENTITYNAME'), 'VAR')
                                    // .appendField('to');
                                    this.appendDummyInput()
                                      .appendField('create entity with')
                                      .appendField(
                                        new NonEditableFieldVariable(
                                          'NAN',
                                          undefined,
                                          ['ENTITYNAME'],
                                          'ENTITYNAME'
                                        ),
                                        'VAR'
                                      );
                                       this.appendValueInput('VALUE')
                                    .setCheck('ENTITY_REFERENCE_SELECTION')
                                    .appendField('Entity Reference Selection: ');

                                     this.appendValueInput('ENTITY')
                                    .setCheck('ENTITY')
                                    .appendField('Output Assign To Entity: ');
                                    this.appendValueInput('FLAG')
                                    .setCheck('Boolean')
                                    .appendField('Sucess Flag: ');
                                    
                            
                                    // this.setOutput(true, 'ENTITY');
                                    this.setPreviousStatement(true, null);
                                    this.setNextStatement(true, null);
                                    this.setColour('#ffd700');
                                    this.setTooltip('Create a Entity in db');
                                    this.setHelpUrl('');
                                    AtlasFieldBridge.attachEntityGuard && AtlasFieldBridge.attachEntityGuard(this); // A6/#1: unplug a wrong-type destination in the ENTITY socket
                                  },
                                };
                            
                                (javascriptGenerator as any).forBlock['create_entity'] = function (
                                  block: any
                                ) {
                                  var variableName = (javascriptGenerator as any).nameDB_.getName(
                                    block.getFieldValue('VAR'),
                                    'VARIABLE'
                                  );
                                    var value =
                                  (javascriptGenerator as any).valueToCode(
                                    block,
                                    'VALUE',
                                    (javascriptGenerator as any).ORDER_ASSIGNMENT
                                  ) || 'NAN';
                                    var entity =
                                  (javascriptGenerator as any).valueToCode(
                                    block,
                                    'ENTITY',
                                    (javascriptGenerator as any).ORDER_ASSIGNMENT
                                  ) || 'NAN_ENTITY_VARIABLE';

                                      var flag =
                                  (javascriptGenerator as any).valueToCode(
                                    block,
                                    'FLAG',
                                    (javascriptGenerator as any).ORDER_ASSIGNMENT
                                  ) || 'NAN';


                                  if(value === 'NAN') {
                                    value = '';
                                  }
                                  var code =  "var updatedEntityUniqueForCreateEntity =  await this.createEntity(this.getEntityById('" +
                                      variableName +
                                      "') , " +
                                      variableName + "$ , $$CRUD_ENTITY_REFERENCE_SELECTION_START$$ " + value +
                                      ' $$CRUD_ENTITY_REFERENCE_SELECTION_END$$)\n';
                                      if(entity !== 'NAN_ENTITY_VARIABLE') {
                                          code = code + entity +
                                    '$data = updatedEntityUniqueForCreateEntity.data;\n  ';
                                      }
                                      if(flag !== 'NAN') {
                                        code = code + flag + ' = updatedEntityUniqueForCreateEntity.flag;\n  ';
                                      }
                                      code = code + ' await this.reinitializeComponents();';

                                      console.log(code);
                                  if (variableName !== 'NAN')
                                    // return [(
                                  return(
                                    code
                                    );
                                    // ), (javascriptGenerator as any).ORDER_ATOMIC];
                                  // return ['false', (javascriptGenerator as any).ORDER_ATOMIC];
                                  return '';
                                };
                            
                                class NonEditableFieldVariable extends Blockly.FieldVariable {
                                  constructor(
                                    variableName: any,
                                    validator: any,
                                    variableTypes: any,
                                    defaultType: any
                                  ) {
                                    super(variableName, validator, variableTypes, defaultType);
                                  }
                            
                                  // Override the showEditor_ method to disable editing
                                  override showEditor_() {
            // click the dropdown → open Atlas (same action as the swap-variable button)
            AtlasFieldBridge.open && AtlasFieldBridge.open((this as any).getSourceBlock());
          }
                                }
                              },
                               customBlockForAsyncFunction() {
                               Blockly.Blocks['async_function_wrapper'] = {
                                    init: function () {

                                       this.appendDummyInput()
                                        .appendField('async group')
                                        .appendField(
                                          new Blockly.FieldDropdown([
                                            ['async_group_A', 'async_group_A'],
                                            ['async_group_B', 'async_group_B'],
                                            ['async_group_C', 'async_group_C'],
                                            ['async_group_D', 'async_group_D'],
                                            ['async_group_E', 'async_group_E'],
                                            ['async_group_F', 'async_group_F'],
                                            ['async_group_G', 'async_group_G'],
                                            ['async_group_H', 'async_group_H'],
                                            ['async_group_I', 'async_group_I'],
                                            ['async_group_J', 'async_group_J'],
                                            ['async_group_K', 'async_group_K'],
                                          ]),
                                          'ASYNC_GROUP'
                                        );
                                        this.appendStatementInput('TRY')
                                            .appendField('try');

                                          this.appendStatementInput('CATCH')
                                            .appendField('catch (error)');

                                          this.appendStatementInput('FINALLY')
                                            .appendField('finally');

                                          this.setPreviousStatement(true, null);
                                          this.setNextStatement(true, null);
                                      this.setColour('#4a90e2');
                                      this.setTooltip('Async function container');
                                      this.setHelpUrl('');
                                    },
                                  };
                            
                            (javascriptGenerator as any).forBlock['async_function_wrapper'] = function (
                                      block: any
                                    ) {
                                      const tryCode = (javascriptGenerator as any).statementToCode(block, 'TRY');
                                      const catchCode = (javascriptGenerator as any).statementToCode(block, 'CATCH');
                                      const finallyCode = (javascriptGenerator as any).statementToCode(
                                        block,
                                        'FINALLY'
                                      );

                                      const groupName = block.getFieldValue('ASYNC_GROUP');

                                      const code = `
                                        this.__asyncGroups = this.__asyncGroups || {};
                                        this.__asyncGroups['${groupName}'] = this.__asyncGroups['${groupName}'] || [];
                                        var __p = (async () => {
                                        try { 
                                          ${tryCode}
                                          } catch(e) { ${catchCode}} finally { ${finallyCode} this.reinitializeComponentsNow(); }
                                        })();

                                         this.__asyncGroups['${groupName}'].push(__p);
                                      `;

                                      return code;
                                    };

                              },
                              customBlockForResolvePromiseByGroup() {
                                  Blockly.Blocks['resolve_promise_by_group'] = {
                                              init: function () {

                                                this.appendDummyInput()
                                                  .appendField('when async group')
                                                  .appendField(
                                                    new Blockly.FieldDropdown([
                                                      ['async_group_A', 'async_group_A'],
                                                      ['async_group_B', 'async_group_B'],
                                                      ['async_group_C', 'async_group_C'],
                                                      ['async_group_D', 'async_group_D'],
                                                      ['async_group_E', 'async_group_E'],
                                                      ['async_group_F', 'async_group_F'],
                                                      ['async_group_G', 'async_group_G'],
                                                      ['async_group_H', 'async_group_H'],
                                                      ['async_group_I', 'async_group_I'],
                                                      ['async_group_J', 'async_group_J'],
                                                      ['async_group_K', 'async_group_K'],
                                                    ]),
                                                    'ASYNC_GROUP'
                                                  )
                                                  .appendField('completed, do');

                                                this.appendStatementInput('BODY')
                                                  .setCheck(null);

                                                this.setPreviousStatement(true, null);
                                                this.setNextStatement(true, null);

                                                this.setColour('#009688');
                                                this.setTooltip('Run enclosed blocks when the selected async group completes (non-blocking)');
                                                this.setHelpUrl('');
                                              },
                                            };
                                             (javascriptGenerator as any).forBlock['resolve_promise_by_group'] =
                                                function (block: any) {

                                                  const groupName = block.getFieldValue('ASYNC_GROUP');
                                                  const bodyCode = javascriptGenerator.statementToCode(block, 'BODY');

                                                  return `
                                                    (async () => {
                                                      if (this.__asyncGroups && this.__asyncGroups['${groupName}']) {
                                                        const __snapshot = [...this.__asyncGroups['${groupName}']];
                                                        await Promise.all(__snapshot);
                                                      }
                                                      ${bodyCode} this.reinitializeComponentsNow();
                                                    })();
                                                  `;

                              }
                            } ,
        customBlockForSelectEntityReference() {

  // Clickable "ⓘ" help field: tooltips don't render in this editor, so clicking shows the legend
  // (stored on the instance as `legendText`) in a native dialog. Same pattern as condition-blocks.
  class HelpField extends Blockly.FieldTextInput {
    override showEditor_() { try { (window as any).alert((this as any).legendText || ''); } catch { /* no-op */ } }
  }
  const makeHelp = (legend: string): any => {
    const f: any = new HelpField('ⓘ');
    f.legendText = legend;
    return f;
  };

Blockly.Blocks['entity_reference_selection'] = {
  init: function () {
    // ── which reference field to configure (+ clickable ⓘ legend; tooltips don't render here) ──
    // LOOK-ONLY relabel (2026-07-04): field names (VAR / CONSIDER_ENTITY / ENTITY / NEXT), types,
    // the generator and the runtime (sanitizeDataAndEntityForEntityReference) are all UNCHANGED.
    this.appendDummyInput()
      .appendField(makeHelp(
        'Configures ONE reference field of the entity you are creating or updating.\n\n' +
        'CREATE:\n' +
        '  • Link to an existing entity  → the field is set to the chosen entity (by its id).\n' +
        '  • Build it inline             → the referenced entity is built from its own data.\n' +
        '  References you do not add here are left out of the create.\n\n' +
        'UPDATE:\n' +
        '  Everything on the entity is saved. Attach this only to point ONE reference at a\n' +
        '  different existing entity — give it an entity with a valid id. Otherwise every\n' +
        '  reference keeps and re-saves its current value.\n\n' +
        'Use "then set another field" to configure more reference fields.'
      ), 'HELP')
      .appendField('reference field')
      .appendField(
        new NonEditableFieldVariable(
          'NAN',
          undefined,
          ['ENTITYREFERENCEFORCRUD'],
          'ENTITYREFERENCEFORCRUD'
        ),
        'VAR'
      );

    // ── mode: link to an existing entity (set by id) vs. build it inline ──
    // Labelled two-option dropdown (Blockly has no radio field). CONSIDER_ENTITY keeps its name +
    // TRUE/FALSE values for back-compat with saved functions; TRUE=link, FALSE=build. Default FALSE
    // preserves the old checkbox default. Hidden + forced TRUE under update_entity (see setOnChange below).
    this.appendDummyInput('MODE_ROW')
      .appendField(
        new Blockly.FieldDropdown([
          ['Link to an existing entity', 'TRUE'],
          ['Build it inline', 'FALSE'],
        ]),
        'CONSIDER_ENTITY'
      );

    // ── the entity to link (used when the box above is checked) ──
    this.appendValueInput('ENTITY')
      .setCheck('ENTITY')
      .appendField('entity');

    // ── chain another reference field ──
    this.appendValueInput('NEXT')
      .setCheck('ENTITY_REFERENCE_SELECTION')
      .appendField('then set another field');

    this.setOutput(true, 'ENTITY_REFERENCE_SELECTION');
    this.setColour('#745ba5');
    this.setTooltip('Configure a reference field when creating or updating an entity');
    AtlasFieldBridge.attachEntityGuard && AtlasFieldBridge.attachEntityGuard(this); // A3/#13: unplug a wrong-entity child dragged into the ENTITY socket
    // COMPOSE with the guard's onchange (installEntityGuard also calls setOnChange — must not clobber it):
    // capture the handler the guard just installed, then run BOTH. This handler hides the mode row and
    // forces link-mode when the block sits under update_entity (update = retarget-only, no build/checkbox).
    const _guardOnChange = (this as any).onchange;
    this.setOnChange(function (this: any, event: any) {
      if (_guardOnChange) { try { _guardOnChange.call(this, event); } catch { /* guard is best-effort */ } }
      const parent = this.getParent && this.getParent();
      const underUpdate = !!parent && parent.type === 'update_entity';
      const row = this.getInput('MODE_ROW');
      if (row && row.isVisible() === underUpdate) row.setVisible(!underUpdate);
      if (underUpdate && this.getFieldValue('CONSIDER_ENTITY') !== 'TRUE') {
        this.setFieldValue('TRUE', 'CONSIDER_ENTITY'); // update = link/override only
      }
      try { if (this.rendered) this.render(); } catch { /* render queue not ready (e.g. mid-test) — visibility still applied */ }
    });
  }
};
  /* ===============================
     CODE GENERATOR
  ================================ */
  (javascriptGenerator as any).forBlock['entity_reference_selection'] = function (block: any) {
 
        const value =
        block.getFieldValue('VAR');

      const considerEntityFlag =
        block.getFieldValue('CONSIDER_ENTITY') === 'TRUE';

      const entityCode =
        (javascriptGenerator as any).valueToCode(
          block,
          'ENTITY',
          (javascriptGenerator as any).ORDER_ATOMIC
        );
         const nextCode =
        (javascriptGenerator as any).valueToCode(
          block,
          'NEXT',
          (javascriptGenerator as any).ORDER_ATOMIC
        );
          const code = `{
      value: '${value}',
      mode: '${considerEntityFlag ? 'link' : 'build'}',
      considerEntityFlag: ${considerEntityFlag},
      entityVariable: '${entityCode}',
    }${nextCode ? ', ' + nextCode : ''}`;

    return [code, (javascriptGenerator as any).ORDER_ATOMIC];

  };
      class NonEditableFieldVariable extends Blockly.FieldVariable {
                                  constructor(
                                    variableName: any,
                                    validator: any,
                                    variableTypes: any,
                                    defaultType: any
                                  ) {
                                    super(variableName, validator, variableTypes, defaultType);
                                  }

                                  // Override the showEditor_ method to disable editing
                                  override showEditor_() {
            // click the dropdown → open Atlas (same action as the swap-variable button)
            AtlasFieldBridge.open && AtlasFieldBridge.open((this as any).getSourceBlock());
          }
                                }
},

  /**
   * Walk a sibling chain of AAOB blocks (aaob_filter_by_label / aaob_label_exists),
   * merge their conditions per AAOB field name and systemKey, then emit one merged
   * shorthand entry per field. Stops at the first non-AAOB sibling and appends its
   * generated code (which handles the rest of the chain via its own sibling logic).
   *
   * This avoids duplicate object keys when multiple AAOB blocks target the same
   * field name — JS would silently drop all but the last duplicate key.
   */
  _collectAaobChainCode(startBlock: any): string {
    const AAOB_TYPES = new Set(['aaob_filter_by_label', 'aaob_label_exists']);
    // fieldName -> keyExpr -> operator fragments[]
    const conditions: Record<string, Record<string, string[]>> = {};
    let current: any = startBlock;
    let lastAaobBlock: any = startBlock;

    while (current && AAOB_TYPES.has(current.type)) {
      const rawVar: string = current.getFieldValue('VAR') || '';
      const fieldName = rawVar === 'NANAAOB_FIELD' ? '' : rawVar;

      if (fieldName) {
        const key =
          (javascriptGenerator as any).valueToCode(
            current, 'KEY', (javascriptGenerator as any).ORDER_NONE
          ) || "''";

        if (!conditions[fieldName]) conditions[fieldName] = {};
        if (!conditions[fieldName][key]) conditions[fieldName][key] = [];

        if (current.type === 'aaob_filter_by_label') {
          const op = current.getFieldValue('OP');
          const value =
            (javascriptGenerator as any).valueToCode(
              current, 'VALUE', (javascriptGenerator as any).ORDER_NONE
            ) || "''";
          const typeAssertion = current.getFieldValue('TYPE');
          const typeFragment =
            typeAssertion && typeAssertion !== '' ? `, _type: '${typeAssertion}'` : '';
          conditions[fieldName][key].push(`${op}: ${value}${typeFragment}`);
        } else {
          conditions[fieldName][key].push('_has_key: true');
        }
      }

      lastAaobBlock = current;
      const sibInput = current.getInput('SIBILING_CONDITIONS');
      current = sibInput?.connection?.targetBlock() || null;
    }

    // Build merged code — one entry per AAOB field, one sub-entry per systemKey
    let code = '';
    for (const [fieldName, keys] of Object.entries(conditions)) {
      const keyEntries = Object.entries(keys)
        .map(([keyExpr, ops]) => `[${keyExpr}]: { ${ops.join(', ')} }`)
        .join(', ');
      code += `${fieldName}: { system_aaobCache: { ${keyEntries} } }, `;
    }

    // Append trailing non-AAOB sibling code (triggers the non-AAOB block's generator)
    const trailingCode =
      (javascriptGenerator as any).valueToCode(
        lastAaobBlock, 'SIBILING_CONDITIONS', (javascriptGenerator as any).ORDER_ASSIGNMENT
      ) || '';

    return code + trailingCode;
  },

  /**
   * AAOB filter block — emits a where-clause fragment that targets the AAOB
   * filter cache column on the parent table via the `system_aaobCache` shorthand.
   *
   * Shorthand emitted: <aaobName>: { system_aaobCache: { [<key>]: { <op>: <value>[, _type: '<type>'] } } }
   * CrudService.convertToWhere rewrites the shorthand into the actual JSONB ops
   * against `<aaobName>_filter_cache_system` (which stores `{ v, t }` per systemKey).
   *
   * v1 operators: _eq, _neq, _in. Range filters (_gt/_lt/_gte/_lte) are deferred to Phase 2.
   * `_has_key` lives in its own block (`aaob_label_exists`).
   *
   * See docs/plans/aaob-filter-cache.md §6.3 (shorthand) and §6.4 (operator support).
   */
  customBlockForAAOBFilterByLabel() {
    // The VAR field is an empty NonEditableFieldVariable — clicking does
    // nothing. The selection UI lives in the Angular HTML panel that appears
    // on block selection (allowedType === 'AAOB_FILTER'); see
    // blockly-interface.component.html and the SELECTED handler +
    // toBeAddedVaraibleChanged() in blockly-interface.component.ts.
    // We use NonEditableFieldVariable (not FieldDropdown) to match the
    // codebase pattern and avoid the "Return To Dashboard" hijack on
    // FieldVariable's rename/delete menu entries.
    Blockly.Blocks['aaob_filter_by_label'] = {
      init: function () {
        const baseOps: [string, string][] = [
          ['equals', '_eq'],
          ['not equals', '_neq'],
          ['in (list)', '_in'],
        ];
        const rangeOps: [string, string][] = [
          ['greater than', '_gt'],
          ['greater or equal', '_gte'],
          ['less than', '_lt'],
          ['less or equal', '_lte'],
        ];

        this.appendDummyInput()
          .appendField('AAOB filter:')
          .appendField(
            new NonEditableFieldVariable(
              'NANAAOB_FIELD',
              undefined,
              ['AAOB_FIELD'],
              'AAOB_FIELD'
            ),
            'VAR'
          );
        this.appendValueInput('KEY')
          .setCheck(['String'])
          .appendField('systemKey');
        this.appendDummyInput('OP_ROW')
          .appendField(
            new Blockly.FieldDropdown([...baseOps, ...rangeOps]),
            'OP'
          );
        this.appendValueInput('VALUE')
          .setCheck(['String', 'Number', 'Boolean', 'Array']);
        this.appendDummyInput()
          .appendField('type')
          .appendField(
            new Blockly.FieldDropdown(
              [
                ['(none)', ''],
                ['String', 'String'],
                ['Number', 'Number'],
                ['Boolean', 'Boolean'],
                ['TimeStamp', 'TimeStamp'],
              ],
              function (this: any, newType: string): string | undefined {
                const block = this.getSourceBlock();
                if (!block) return newType;
                const opField = block.getField('OP') as any;
                if (!opField) return newType;
                const currentOp = opField.getValue();
                const supportsRange = newType === 'Number' || newType === 'TimeStamp';
                const newOptions: [string, string][] = supportsRange
                  ? [...baseOps, ...rangeOps]
                  : [...baseOps];
                opField.menuGenerator_ = newOptions;
                if (!newOptions.some(([, val]) => val === currentOp)) {
                  opField.setValue('_eq');
                }
                return newType;
              }
            ),
            'TYPE'
          );
        this.appendValueInput('SIBILING_CONDITIONS')
          .setCheck(['QUERY_VARIABLE', 'OR'])
          .appendField('Sibling');
        this.setOutput(true, 'QUERY_VARIABLE');
        this.setColour('#000000');
        this.setTooltip('Filter parent rows by an AAOB systemKey value (uses the filter cache).');
        this.setHelpUrl('');
      },
    };

    (javascriptGenerator as any).forBlock['aaob_filter_by_label'] = function (block: any) {
      // If this block is a sibling of another AAOB block, the first block in the
      // chain already handled code generation for the entire chain — return empty.
      const parentConn = block.outputConnection?.targetConnection;
      const parentBlock = parentConn?.getSourceBlock();
      if (
        parentBlock &&
        (parentBlock.type === 'aaob_filter_by_label' || parentBlock.type === 'aaob_label_exists') &&
        parentConn === parentBlock.getInput('SIBILING_CONDITIONS')?.connection
      ) {
        return ['', (javascriptGenerator as any).ORDER_ATOMIC];
      }

      return [QueryFns._collectAaobChainCode(block), (javascriptGenerator as any).ORDER_ATOMIC];
    };

    class NonEditableFieldVariable extends Blockly.FieldVariable {
      constructor(
        variableName: any,
        validator: any,
        variableTypes: any,
        defaultType: any
      ) {
        super(variableName, validator, variableTypes, defaultType);
      }
      override showEditor_() {
            // click the dropdown → open Atlas (same action as the swap-variable button)
            AtlasFieldBridge.open && AtlasFieldBridge.open((this as any).getSourceBlock());
          }
    }
  },

  /**
   * AAOB label-exists block — emits a where-clause fragment that checks whether a
   * given systemKey exists at all on the parent's AAOB (via JSONB `_has_key`).
   *
   * Shorthand emitted: <aaobName>: { system_aaobCache: { [<key>]: { _has_key: true } } }
   *
   * See docs/plans/aaob-filter-cache.md §6.3 / §6.4.
   */
  customBlockForAAOBLabelExists() {
    // See customBlockForAAOBFilterByLabel above — same pattern: empty
    // NonEditableFieldVariable placeholder, selection driven by HTML panel.
    Blockly.Blocks['aaob_label_exists'] = {
      init: function () {
        this.appendDummyInput()
          .appendField('AAOB has systemKey:')
          .appendField(
            new NonEditableFieldVariable(
              'NANAAOB_FIELD',
              undefined,
              ['AAOB_FIELD'],
              'AAOB_FIELD'
            ),
            'VAR'
          );
        this.appendValueInput('KEY').setCheck(['String']).appendField('systemKey');
        this.appendValueInput('SIBILING_CONDITIONS')
          .setCheck(['QUERY_VARIABLE', 'OR'])
          .appendField('Sibling');
        this.setOutput(true, 'QUERY_VARIABLE');
        this.setColour('#000000');
        this.setTooltip('Filter parent rows where an AAOB systemKey exists.');
        this.setHelpUrl('');
      },
    };

    (javascriptGenerator as any).forBlock['aaob_label_exists'] = function (block: any) {
      // If this block is a sibling of another AAOB block, the first block in the
      // chain already handled code generation for the entire chain — return empty.
      const parentConn = block.outputConnection?.targetConnection;
      const parentBlock = parentConn?.getSourceBlock();
      if (
        parentBlock &&
        (parentBlock.type === 'aaob_filter_by_label' || parentBlock.type === 'aaob_label_exists') &&
        parentConn === parentBlock.getInput('SIBILING_CONDITIONS')?.connection
      ) {
        return ['', (javascriptGenerator as any).ORDER_ATOMIC];
      }

      return [QueryFns._collectAaobChainCode(block), (javascriptGenerator as any).ORDER_ATOMIC];
    };

    class NonEditableFieldVariable extends Blockly.FieldVariable {
      constructor(
        variableName: any,
        validator: any,
        variableTypes: any,
        defaultType: any
      ) {
        super(variableName, validator, variableTypes, defaultType);
      }
      override showEditor_() {
            // click the dropdown → open Atlas (same action as the swap-variable button)
            AtlasFieldBridge.open && AtlasFieldBridge.open((this as any).getSourceBlock());
          }
    }
  },

  customBlockForAAOBSortByLabel() {
    Blockly.Blocks['aaob_sort_by_label'] = {
      init: function () {
        this.appendDummyInput()
          .appendField('AAOB sort:')
          .appendField(
            new NonEditableFieldVariable(
              'NANAAOB_FIELD',
              undefined,
              ['AAOB_FIELD'],
              'AAOB_FIELD'
            ),
            'VAR'
          );
        this.appendValueInput('KEY')
          .setCheck(['String'])
          .appendField('systemKey');
        this.appendDummyInput()
          .appendField('direction')
          .appendField(
            new Blockly.FieldDropdown([
              ['Ascending', 'asc'],
              ['Ascending nulls last', 'asc_nulls_last'],
              ['Descending', 'desc'],
              ['Descending nulls last', 'desc_nulls_last'],
            ]),
            'DIR'
          );
        this.appendDummyInput()
          .appendField('type')
          .appendField(
            new Blockly.FieldDropdown([
              ['String', 'String'],
              ['Number', 'Number'],
              ['Boolean', 'Boolean'],
              ['TimeStamp', 'TimeStamp'],
            ]),
            'TYPE'
          );
        this.setOutput(true, 'ORDER');
        this.setColour('#1a1a2e');
        this.setTooltip('Sort parent rows by an AAOB systemKey value (uses computed field).');
        this.setHelpUrl('');
      },
    };

    (javascriptGenerator as any).forBlock['aaob_sort_by_label'] = function (block: any) {
      const rawVar: string = block.getFieldValue('VAR') || '';
      const aaobFieldName = rawVar === 'NANAAOB_FIELD' ? '' : rawVar;
      const key =
        (javascriptGenerator as any).valueToCode(
          block,
          'KEY',
          (javascriptGenerator as any).ORDER_NONE
        ) || "''";
      const dir = block.getFieldValue('DIR');
      const type = block.getFieldValue('TYPE');

      if (!aaobFieldName) {
        return ['0', (javascriptGenerator as any).ORDER_ATOMIC];
      }

      const code = `{ ${aaobFieldName}: { system_aaobSort: { [${key}]: '${dir}', _type: '${type}' } } }`;
      return [code, (javascriptGenerator as any).ORDER_ATOMIC];
    };

    class NonEditableFieldVariable extends Blockly.FieldVariable {
      constructor(
        variableName: any,
        validator: any,
        variableTypes: any,
        defaultType: any
      ) {
        super(variableName, validator, variableTypes, defaultType);
      }
      override showEditor_() {
            // click the dropdown → open Atlas (same action as the swap-variable button)
            AtlasFieldBridge.open && AtlasFieldBridge.open((this as any).getSourceBlock());
          }
    }
  },

  /**
   * AAOB-label aggregate block — avg/sum/min/max over a Number-typed AAOB
   * systemKey. Emits a call to CrudService.aaobAggregateEntity which constructs
   * the `_cf_<entity>_<aaob>_<systemKey>_num` computed field name and delegates
   * to aggregateEntity. Type-mismatched rows are filtered out by the SQL
   * function body created by Phase C.
   *
   * count uses a _has_key WHERE filter under the hood (counts rows where systemKey exists).
   *
   * See docs/plans/query-capabilities-phase-2.md §4.5.
   */
  customBlockForAAOBAggregateByLabel() {
    Blockly.Blocks['aaob_aggregate_by_label'] = {
      init: function () {
        this.appendDummyInput()
          .appendField('AAOB Aggregate Entity:')
          .appendField(
            new NonEditableFieldVariable(
              'NANENTITY',
              undefined,
              ['ENTITYNAME'],
              'ENTITYNAME'
            ),
            'VAR'
          );
        this.appendDummyInput()
          .appendField('AAOB:')
          .appendField(
            new NonEditableFieldVariable(
              'NANAAOB_FIELD',
              undefined,
              ['AAOB_FIELD'],
              'AAOB_FIELD'
            ),
            'AAOB_FIELD'
          );
        this.appendValueInput('KEY')
          .setCheck(['String'])
          .appendField('systemKey');
        this.appendDummyInput()
          .appendField('Op:')
          .appendField(
            new Blockly.FieldDropdown([
              ['average', 'avg'],
              ['sum', 'sum'],
              ['min', 'min'],
              ['max', 'max'],
              ['count', 'count'],
            ]),
            'OP'
          );
        this.appendValueInput('CONDITIONS')
          .setCheck(['OR', 'QUERY_VARIABLE'])
          .appendField('Where Conditions');
        this.appendValueInput('EC')
          .setCheck(['ENTITYCONDITIONS'])
          .appendField('Child Entites:');
        this.setOutput(true, 'Number');
        this.setColour('#1a1a2e');
        this.setTooltip('Aggregate (avg/sum/min/max/count) over a Number-typed AAOB systemKey.');
        this.setHelpUrl('');
      },
    };

    (javascriptGenerator as any).forBlock['aaob_aggregate_by_label'] = function (block: any) {
      const entityId = block.getFieldValue('VAR');
      const rawAaob: string = block.getFieldValue('AAOB_FIELD') || '';
      const aaobFieldName = rawAaob === 'NANAAOB_FIELD' ? '' : rawAaob;
      const key =
        (javascriptGenerator as any).valueToCode(
          block,
          'KEY',
          (javascriptGenerator as any).ORDER_NONE
        ) || "''";
      const op = block.getFieldValue('OP');
      const conditions =
        (javascriptGenerator as any).valueToCode(
          block,
          'CONDITIONS',
          (javascriptGenerator as any).ORDER_ASSIGNMENT
        ) || '';
      const additionalChildConditions =
        (javascriptGenerator as any).valueToCode(
          block,
          'EC',
          (javascriptGenerator as any).ORDER_ASSIGNMENT
        ) || '';

      if (entityId === 'NANENTITY' || !aaobFieldName) {
        return ['null', (javascriptGenerator as any).ORDER_ATOMIC];
      }

      const whereInner = conditions + ' ' + additionalChildConditions;
      const hasWhere = whereInner.trim().length > 0;
      const variablesArg = hasWhere
        ? `, { ${entityId}: { where: { ${whereInner} } } }`
        : '';

      const code =
        `await this.aaobAggregateEntity(this.getEntityById('${entityId}'), '${op}', '${aaobFieldName}', ${key}${variablesArg})`;
      return [code, (javascriptGenerator as any).ORDER_ATOMIC];
    };

    class NonEditableFieldVariable extends Blockly.FieldVariable {
      constructor(
        variableName: any,
        validator: any,
        variableTypes: any,
        defaultType: any
      ) {
        super(variableName, validator, variableTypes, defaultType);
      }
      override showEditor_() {
            // click the dropdown → open Atlas (same action as the swap-variable button)
            AtlasFieldBridge.open && AtlasFieldBridge.open((this as any).getSourceBlock());
          }
    }
  }


};